import { useRef, useMemo, useEffect } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useTexture, useScroll } from '@react-three/drei'
import * as THREE from 'three'
import { useControls } from 'leva'
import { HOARD } from './boardLayout'
import { useModal } from './ModalContext'
import { UpsideDownModal, WhatIBringModal } from './modals'

// Diegetic courtside LED hoarding on the horizon. Each "sponsor" is a full-board banner ad
// for one of Nauman's games / skills; they scroll behind the central scoreboard.

const { W: BOARD_W, H: BOARD_H, Y: BOARD_Y, Z: BOARD_Z } = HOARD
const SPEED = 4.5     // world units / second
const GAP = 0         // banners flush against each other

// TODO: confirm the links below (games are set; naumanshah / figma / hireme point at LinkedIn for now, mirrorgame unlinked)
const ADS = [
  { img: './Ads/naumanshah.png',    act: 'linkedin' },
  { img: './Ads/warofnova.png',     act: 'warofnova' },
  { img: './Ads/figma.png',         act: 'ux' },
  { img: './Ads/hatchkings.png',    act: 'hatchkings' },
  { img: './Ads/totalfootball.png', act: 'totalfootball' },
  { img: './Ads/mirrorgame.png',    act: 'mirror' },
  { img: './Ads/hireme.png',        act: 'contact' },
]

function Banner({ tex, w, x, onClick, planes }) {
  const over = () => { if (onClick) document.body.style.cursor = 'pointer' }
  const out = () => { document.body.style.cursor = 'auto' }
  return (
    <mesh position={[x, 0, 0.02]} onClick={onClick} onPointerOver={over} onPointerOut={out}>
      <planeGeometry args={[w, BOARD_H]} />
      <meshBasicMaterial map={tex} transparent clippingPlanes={planes} toneMapped={false} />
    </mesh>
  )
}

export default function SponsorBoards() {
  const scroller = useRef()
  const { gl } = useThree()
  const scroll = useScroll()
  const { openModal } = useModal()
  const { ribbonColor, contactScroll } = useControls('Ad board', {
    ribbonColor: '#1b232e',
    contactScroll: { value: 1, min: 0, max: 1, step: 0.01 },   // where "hire him" scrolls to (1 = bottom)
  })
  useEffect(() => { gl.localClippingEnabled = true }, [gl])

  const ext = (url) => window.open(url, '_blank', 'noopener,noreferrer')
  const ACT = {
    linkedin: () => ext('https://www.linkedin.com/in/naumanshah1220/'),
    warofnova: () => ext('https://warofnova.com/'),
    hatchkings: () => ext('https://hatchkings.revolvinggames.com/?cache=false'),
    totalfootball: () => ext('https://naumanshah1220.github.io/total-football/'),
    ux: () => openModal(<WhatIBringModal focus="ux" />),
    mirror: () => openModal(<UpsideDownModal />),
    contact: () => {
      const el = scroll?.el; if (!el) return
      const start = el.scrollTop
      const target = contactScroll * (el.scrollHeight - el.clientHeight)
      const t0 = performance.now(), dur = 1100
      const step = (t) => {                                   // drive scrollTop ourselves so drei reads + damps it like a real scroll
        const k = Math.min(1, (t - t0) / dur)
        const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2   // ease-in-out
        el.scrollTop = start + (target - start) * e
        if (k < 1) requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
    },
  }

  const planes = useMemo(() => [
    new THREE.Plane(new THREE.Vector3(1, 0, 0), BOARD_W / 2),   // x >= -W/2
    new THREE.Plane(new THREE.Vector3(-1, 0, 0), BOARD_W / 2),  // x <=  W/2
  ], [])

  const texes = useTexture(ADS.map((a) => a.img))

  // lay banners end-to-end at their real aspect ratios (each fills the board height), centred, then loop
  const { items, total } = useMemo(() => {
    let x = 0; const arr = []
    ADS.forEach((ad, i) => {
      const t = texes[i]
      const aspect = t?.image ? t.image.width / t.image.height : 5
      const w = BOARD_H * aspect
      arr.push({ ad, tex: t, w, cx: x + w / 2 })
      x += w + GAP
    })
    const total = x
    return { items: arr.map((it) => ({ ...it, cx: it.cx - total / 2 })), total }
  }, [texes])

  useFrame((_, delta) => {
    if (!scroller.current) return
    scroller.current.position.x -= Math.min(delta, 0.05) * SPEED
    if (scroller.current.position.x <= -total) scroller.current.position.x += total
  })

  // two back-to-back copies for a seamless scroll
  const slots = useMemo(() => [...items, ...items.map((it) => ({ ...it, cx: it.cx + total }))], [items, total])

  return (
    <group position={[0, BOARD_Y, BOARD_Z]}>
      {/* LED housing (spans the screen) + rails */}
      <mesh position={[0, 0, -0.1]}>
        <planeGeometry args={[BOARD_W, BOARD_H]} />
        <meshBasicMaterial color={ribbonColor} toneMapped={false} />
      </mesh>
      <mesh position={[0, BOARD_H / 2 + 0.18, -0.06]}>
        <planeGeometry args={[BOARD_W, 0.32]} />
        <meshBasicMaterial color="#2f3a47" toneMapped={false} />
      </mesh>
      <mesh position={[0, -BOARD_H / 2 - 0.18, -0.06]}>
        <planeGeometry args={[BOARD_W, 0.32]} />
        <meshBasicMaterial color="#2f3a47" toneMapped={false} />
      </mesh>

      <group ref={scroller}>
        {slots.map((s, i) => (
          <Banner key={i} tex={s.tex} w={s.w} x={s.cx} onClick={ACT[s.ad.act]} planes={planes} />
        ))}
      </group>
    </group>
  )
}
